import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCcw } from "lucide-react";

export const DEPLOYMENT_UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const DEPLOYMENT_UPDATE_SNOOZE_MS = 10 * 60 * 1000;

const MAIN_BUNDLE_SELECTOR = 'script[src*="static/js/main."]';

const normalizeAssetPath = (value: string, baseUrl: string) => {
  try {
    const assetUrl = new URL(value, baseUrl);
    return `${assetUrl.pathname}${assetUrl.search}`;
  } catch {
    return "";
  }
};

export const getMainBundlePathFromHtml = (
  html: string,
  baseUrl: string
): string => {
  if (!html || !baseUrl || typeof DOMParser === "undefined") return "";

  const parsed = new DOMParser().parseFromString(html, "text/html");
  const source =
    parsed.querySelector<HTMLScriptElement>(MAIN_BUNDLE_SELECTOR)?.src || "";
  return normalizeAssetPath(source, baseUrl);
};

export const getCurrentMainBundlePath = (
  documentValue: Document,
  baseUrl: string
): string => {
  const source =
    documentValue.querySelector<HTMLScriptElement>(MAIN_BUNDLE_SELECTOR)?.src ||
    "";
  return normalizeAssetPath(source, baseUrl);
};

type DeploymentUpdateCheckOptions = {
  currentBundlePath: string;
  fetchImpl?: typeof fetch;
  pageUrl: string;
  cacheBust?: number;
  signal?: AbortSignal;
};

export const checkForDeploymentUpdate = async ({
  currentBundlePath,
  fetchImpl = fetch,
  pageUrl,
  cacheBust = Date.now(),
  signal,
}: DeploymentUpdateCheckOptions): Promise<boolean> => {
  if (!currentBundlePath || !pageUrl) return false;

  const checkUrl = new URL("/", pageUrl);
  checkUrl.searchParams.set("_cnfood_asset_check", String(cacheBust));

  const response = await fetchImpl(checkUrl.toString(), {
    cache: "no-store",
    credentials: "same-origin",
    headers: { Accept: "text/html" },
    signal,
  });
  if (!response.ok) return false;

  const latestBundlePath = getMainBundlePathFromHtml(
    await response.text(),
    checkUrl.toString()
  );
  return Boolean(
    latestBundlePath && latestBundlePath !== currentBundlePath
  );
};

const DeploymentUpdatePrompt = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const currentBundlePathRef = useRef("");
  const lastCheckedAtRef = useRef(0);
  const snoozeUntilRef = useRef(0);
  const checkingRef = useRef(false);
  const mountedRef = useRef(true);
  const activeCheckRef = useRef<AbortController | null>(null);

  const runUpdateCheck = useCallback(async () => {
    const now = Date.now();
    if (
      checkingRef.current ||
      now < snoozeUntilRef.current ||
      now - lastCheckedAtRef.current < DEPLOYMENT_UPDATE_CHECK_INTERVAL_MS
    ) {
      return;
    }

    if (
      typeof navigator !== "undefined" &&
      navigator.onLine === false
    ) {
      return;
    }

    checkingRef.current = true;
    lastCheckedAtRef.current = now;
    const controller = new AbortController();
    activeCheckRef.current = controller;
    try {
      const hasUpdate = await checkForDeploymentUpdate({
        currentBundlePath: currentBundlePathRef.current,
        pageUrl: window.location.href,
        signal: controller.signal,
      });
      if (hasUpdate && mountedRef.current) setUpdateAvailable(true);
    } catch {
      // A failed background check must not interrupt contracts or payments.
    } finally {
      if (activeCheckRef.current === controller) {
        activeCheckRef.current = null;
      }
      checkingRef.current = false;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    currentBundlePathRef.current = getCurrentMainBundlePath(
      document,
      window.location.href
    );
    if (!currentBundlePathRef.current) return;

    const onFocus = () => void runUpdateCheck();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void runUpdateCheck();
    };
    const onOnline = () => void runUpdateCheck();
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") void runUpdateCheck();
    }, DEPLOYMENT_UPDATE_CHECK_INTERVAL_MS);

    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      mountedRef.current = false;
      activeCheckRef.current?.abort();
      activeCheckRef.current = null;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [runUpdateCheck]);

  if (!updateAvailable) return null;

  const continueCurrentVersion = () => {
    snoozeUntilRef.current = Date.now() + DEPLOYMENT_UPDATE_SNOOZE_MS;
    lastCheckedAtRef.current = 0;
    setUpdateAvailable(false);
  };

  return (
    <section
      role="alertdialog"
      aria-labelledby="deployment-update-title"
      aria-describedby="deployment-update-description"
      className="fixed inset-x-3 bottom-3 z-[10000] mx-auto max-w-xl rounded-2xl border border-blue-200 bg-white p-4 shadow-2xl shadow-slate-900/30 sm:bottom-5 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="mt-0.5 rounded-full bg-blue-50 p-2 text-blue-700"
        >
          <RefreshCcw size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <h2
            id="deployment-update-title"
            className="text-base font-black text-slate-950 sm:text-lg"
          >
            새 버전이 준비되었습니다
          </h2>
          <p
            id="deployment-update-description"
            className="mt-1 text-sm font-semibold leading-6 text-slate-600"
          >
            작성 중인 내용이 있다면 먼저 확인해 주세요. 새로고침하면 최신
            기능과 보안 수정이 적용됩니다.
          </p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <button
          type="button"
          onClick={continueCurrentVersion}
          className="min-h-[48px] rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200"
        >
          10분 후 다시 알림
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="min-h-[48px] rounded-xl bg-blue-700 px-4 py-3 text-sm font-black text-white transition hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200"
        >
          새 버전으로 새로고침
        </button>
      </div>
    </section>
  );
};

export default DeploymentUpdatePrompt;
